import InputField from "components/Common/InputField"
import ThemeModal from "components/Common/ThemeModal"
import FormikCreatableSelect from "components/Common/FormikCreatableSelect"
import { useMemo, useCallback, useState, useEffect, useRef } from "react"
import { toast } from "react-toastify"
import Select from "react-select"
import AsyncSelect from "react-select/async"
import AsyncCreatableSelect from "react-select/async-creatable"
import { Form, FormFeedback, Label, Button, Row, Col, Card, CardBody } from "reactstrap"
import * as Yup from "yup"
import { useFormik } from "formik"
import { useAccountLoanStatuses } from "hooks/useAccountLoanStatus"
import { useAccounts } from "hooks/useAccountEntity"
import { useCreateAccountLoan, useEditAccountLoan } from "hooks/useAccountLoan"
import { getUsers } from "api/userApi"
import { getCustomers } from "api/customerApi"
import { getVehicleImporters } from "api/vehicleImporterApi"
import { getAccountVendors } from "api/accountVendorApi"

const BORROWER_TYPE_OPTIONS = [
  { value: "staff", label: "Staff" },
  { value: "customer", label: "Customer" },
  { value: "importer", label: "Importer" },
  { value: "vendor", label: "Vendor" },
]

const BORROWER_FIELD_BY_TYPE = {
  staff: "borrower_id",
  customer: "borrower_customer_id",
  importer: "borrower_vehicle_importer_id",
  vendor: "borrower_vendor_id",
}

const debouncePromise = (fn, delay = 300) => {
  let timer
  return (...args) =>
    new Promise(resolve => {
      clearTimeout(timer)
      timer = setTimeout(() => resolve(fn(...args)), delay)
    })
}

const toOptions = list => (Array.isArray(list) ? list.map(item => ({ value: item.id, label: item.name })) : [])

const makeLoadOptions = fetchFn =>
  debouncePromise(async inputValue => {
    const res = await fetchFn({ search: inputValue, all: true, per_page: 20 })
    return toOptions(res?.data?.data || res?.data || [])
  }, 300)

const AccountLoanAddEdit = ({ modal, toggleModal, isEdit, editedData }) => {
  const { mutateAsync: createMutation, isPending: createLoading } = useCreateAccountLoan()
  const { mutateAsync: editMutation, isPending: editLoading } = useEditAccountLoan()

  const { data: statusResponse, isLoading: statusLoading } =
    useAccountLoanStatuses({ all: true, per_page: 200 }, modal)
  const { data: accountResponse, isLoading: accountLoading } =
    useAccounts({ all: true, per_page: 200 }, modal)

  const statuses = statusResponse?.data?.data || statusResponse?.data || []
  const accounts = accountResponse?.data?.data || accountResponse?.data || []

  const statusOptions = useMemo(
    () => (Array.isArray(statuses) ? statuses.map(item => ({ value: item.id, label: item.name })) : []),
    [statuses]
  )

  const accountOptions = useMemo(
    () => (Array.isArray(accounts) ? accounts.map(item => ({ value: item.id, label: item.name })) : []),
    [accounts]
  )

  const loadUserOptions = useRef(makeLoadOptions(getUsers)).current
  const loadCustomerOptions = useRef(makeLoadOptions(getCustomers)).current
  const loadImporterOptions = useRef(makeLoadOptions(getVehicleImporters)).current
  const loadVendorOptions = useRef(makeLoadOptions(getAccountVendors)).current

  const initialBorrowerType = editedData?.borrower_type || "staff"

  const validationSchema = Yup.object({
    borrower_type: Yup.string().required("Please select a borrower type"),
    borrower_id: Yup.mixed().when("borrower_type", {
      is: "staff",
      then: schema => schema.required("Please select a borrower"),
      otherwise: schema => schema.nullable(),
    }),
    borrower_customer_id: Yup.mixed().when("borrower_type", {
      is: "customer",
      then: schema => schema.required("Please select a customer"),
      otherwise: schema => schema.nullable(),
    }),
    borrower_vehicle_importer_id: Yup.mixed().when("borrower_type", {
      is: "importer",
      then: schema => schema.required("Please select an importer"),
      otherwise: schema => schema.nullable(),
    }),
    borrower_vendor_id: Yup.mixed().when("borrower_type", {
      is: "vendor",
      then: schema => schema.required("Please select a vendor"),
      otherwise: schema => schema.nullable(),
    }),
    amount: Yup.number().typeError("Amount must be numeric").integer("Amount must be a whole number").min(1, "Amount must be at least 1").required("Please enter loan amount"),
    loan_status_id: Yup.mixed().required("Please select a loan status"),
    loan_date: Yup.string().required("Please select the loan date"),
    purpose: Yup.string().nullable().max(255, "Purpose cannot exceed 255 characters"),
    notes: Yup.string().nullable(),
    account_id: Yup.mixed().required("Please select an account"),
  })

  const onSubmit = useCallback(
    async (values, { resetForm }) => {
      const payload = {
        ...values,
        amount: Number(values.amount),
        purpose: values.purpose?.trim() || null,
        notes: values.notes?.trim() || null,
      }

      const action = isEdit
        ? editMutation({ id: editedData.id, ...payload })
        : createMutation(payload)

      try {
        const res = await action
        toast.success(res?.message ?? `Loan ${isEdit ? "updated" : "created"} successfully.`)
        resetForm()
        toggleModal()
      } catch (error) {
        const msg = error?.response?.data?.message || "Failed to save loan."
        toast.error(msg)
      }
    },
    [isEdit, editedData, editMutation, createMutation, toggleModal]
  )

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      borrower_type: initialBorrowerType,
      borrower_id: editedData?.borrower_id || editedData?.borrower?.id || "",
      borrower_customer_id: editedData?.borrower_customer_id || editedData?.borrower_customer?.id || "",
      borrower_vehicle_importer_id: editedData?.borrower_vehicle_importer_id || editedData?.borrower_importer?.id || "",
      borrower_vendor_id: editedData?.borrower_vendor_id || editedData?.borrower_vendor?.id || "",
      amount: editedData?.amount ?? "",
      loan_status_id: editedData?.loan_status_id || editedData?.loanStatus?.id || "",
      loan_date: editedData?.loan_date || "",
      purpose: editedData?.purpose || "",
      notes: editedData?.notes || "",
      account_id: editedData?.account_id || editedData?.account?.id || "",
    },
    validationSchema,
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit,
  })

  const borrowerType = formik.values.borrower_type
  const borrowerField = BORROWER_FIELD_BY_TYPE[borrowerType]

  // The currently selected borrower's {value, label} — kept independent of the
  // async-loaded option list so an existing (edit) selection always shows its name.
  const [borrowerOption, setBorrowerOption] = useState(null)

  useEffect(() => {
    if (!modal) return

    if (editedData) {
      const labelByType = {
        staff: editedData.borrower?.name,
        customer: editedData.borrower_customer?.name,
        importer: editedData.borrower_importer?.name,
        vendor: editedData.borrower_vendor?.name,
      }
      const id = editedData.borrower_id || editedData.borrower_customer_id ||
        editedData.borrower_vehicle_importer_id || editedData.borrower_vendor_id
      const label = labelByType[editedData.borrower_type]

      setBorrowerOption(id ? { value: id, label: label || `#${id}` } : null)
    } else {
      setBorrowerOption(null)
    }
  }, [modal, editedData])

  const handleBorrowerTypeChange = option => {
    const nextType = option?.value || "staff"
    formik.setValues({
      ...formik.values,
      borrower_type: nextType,
      borrower_id: "",
      borrower_customer_id: "",
      borrower_vehicle_importer_id: "",
      borrower_vendor_id: "",
    })
    setBorrowerOption(null)
  }

  const handleBorrowerChange = option => {
    formik.setFieldValue(borrowerField, option?.value || "")
    setBorrowerOption(option || null)
  }

  const handleBorrowerCreate = inputValue => {
    formik.setFieldValue(borrowerField, inputValue)
    setBorrowerOption({ value: inputValue, label: inputValue })
  }

  const borrowerError = formik.touched[borrowerField] && formik.errors[borrowerField]

  const borrowerFieldMeta = {
    staff: { label: "Borrower", loadOptions: loadUserOptions },
    customer: { label: "Customer", loadOptions: loadCustomerOptions },
    importer: { label: "Importer", loadOptions: loadImporterOptions },
    vendor: { label: "Vendor", loadOptions: loadVendorOptions },
  }[borrowerType]

  const loadingAny = createLoading || editLoading || statusLoading || accountLoading

  return (
    <ThemeModal isOpen={modal} toggle={toggleModal} title={isEdit ? "Edit Loan" : "Add Loan"} size="xl">
      <Form onSubmit={e => { e.preventDefault(); formik.handleSubmit() }}>
        <Card className="border">
          <CardBody>
            <h5 className="mb-3">Loan Information</h5>
            <Row>
              <Col md={6}>
                <div className="mb-3">
                  <Label className="form-label">
                    Borrower Type <span className="text-danger">*</span>
                  </Label>
                  <Select
                    options={BORROWER_TYPE_OPTIONS}
                    value={BORROWER_TYPE_OPTIONS.find(o => o.value === borrowerType) || null}
                    onChange={handleBorrowerTypeChange}
                    isClearable={false}
                  />
                </div>
              </Col>
              <Col md={6}>
                <div className="mb-3">
                  <Label className="form-label">
                    {borrowerFieldMeta.label} <span className="text-danger">*</span>
                  </Label>
                  {borrowerType === "staff" ? (
                    <AsyncCreatableSelect
                      cacheOptions
                      defaultOptions
                      loadOptions={borrowerFieldMeta.loadOptions}
                      value={borrowerOption}
                      onChange={handleBorrowerChange}
                      onCreateOption={handleBorrowerCreate}
                      placeholder="Type to search staff..."
                      isClearable
                    />
                  ) : (
                    <AsyncSelect
                      cacheOptions
                      defaultOptions
                      loadOptions={borrowerFieldMeta.loadOptions}
                      value={borrowerOption}
                      onChange={handleBorrowerChange}
                      placeholder={`Type to search ${borrowerFieldMeta.label.toLowerCase()}...`}
                      isClearable
                    />
                  )}
                  {borrowerError && <div className="text-danger small mt-1">{borrowerError}</div>}
                </div>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <div className="mb-3">
                  <Label htmlFor="amount">Amount</Label>
                  <InputField type="number" name="amount" id="amount" min="1" value={formik.values.amount} onChange={formik.handleChange} invalid={formik.touched.amount && !!formik.errors.amount} />
                  {formik.touched.amount && formik.errors.amount && <FormFeedback type="invalid">{formik.errors.amount}</FormFeedback>}
                </div>
              </Col>
              <Col md={6}><FormikCreatableSelect name="loan_status_id" formik={formik} label="Loan Status" options={statusOptions} loading={statusLoading} required /></Col>
            </Row>
            <Row>
              <Col md={6}><FormikCreatableSelect name="account_id" formik={formik} label="Account" options={accountOptions} loading={accountLoading} required /></Col>
              <Col md={6}>
                <div className="mb-3">
                  <Label htmlFor="loan_date">Loan Date</Label>
                  <InputField type="date" name="loan_date" id="loan_date" value={formik.values.loan_date} onChange={formik.handleChange} invalid={formik.touched.loan_date && !!formik.errors.loan_date} />
                  {formik.touched.loan_date && formik.errors.loan_date && <FormFeedback type="invalid">{formik.errors.loan_date}</FormFeedback>}
                </div>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <div className="mb-3">
                  <Label htmlFor="purpose">Purpose</Label>
                  <InputField type="text" name="purpose" id="purpose" value={formik.values.purpose} onChange={formik.handleChange} invalid={formik.touched.purpose && !!formik.errors.purpose} />
                  {formik.touched.purpose && formik.errors.purpose && <FormFeedback type="invalid">{formik.errors.purpose}</FormFeedback>}
                </div>
              </Col>
            </Row>
          </CardBody>
        </Card>

        <Card className="border mt-3">
          <CardBody>
            <div className="mb-3">
              <Label htmlFor="notes">Notes</Label>
              <InputField type="textarea" name="notes" id="notes" value={formik.values.notes} onChange={formik.handleChange} invalid={formik.touched.notes && !!formik.errors.notes} />
              {formik.touched.notes && formik.errors.notes && <FormFeedback type="invalid">{formik.errors.notes}</FormFeedback>}
            </div>
          </CardBody>
        </Card>

        <div className="text-end mt-3">
          <Button color="success" type="submit" disabled={loadingAny}>
            {loadingAny ? "Saving..." : isEdit ? "Save Changes" : "Add Loan"}
          </Button>
        </div>
      </Form>
    </ThemeModal>
  )
}

export default AccountLoanAddEdit
